/**
 * facet-predicates.ts — queue #640 §UAT-2026-07-07-R640 §R640-2/§R640-4.
 *
 * 카탈로그 패싯 필터(5종) + 문자열 검색의 predicate/derive 순수 함수. `CommonShowcasePage`
 * (visibleNodes selector) · `ModuleFacetBar`(옵션/칩 계산) · E2E 가 동일 헬퍼를 공유해
 * 로직 발산을 막는다(§R640-6). BE 호출 없음 — 전체 `ModuleNode` 응답 필드 또는 빌드타임
 * `take-this.json` 에서 파생(§R640-2 실측). 신규 entity/DDL/endpoint/응답필드 0.
 */
import type { ModuleNode, TakeThisData } from '../types/showcase';

// ─── derive (단일 노드 → 패싯 값) ─────────────────────────────────────────────

/** ② 상태 — ModuleNode.supersededBy 로 파생(#617). ★`!== null` 금지 — JSON 상 생략(undefined) 가능. */
export function deriveStatus(node: ModuleNode): 'active' | 'superseded' {
  return node.supersededBy != null ? 'superseded' : 'active';
}

/** ③ 라이브 데모 유무 — ModuleCatalogTree 의 hasDemo 규칙과 정확히 동일 식(C009 는 info 라우팅이라 데모 아님). */
export function deriveHasDemo(node: ModuleNode): boolean {
  return !!node.demoEndpoint && node.code !== 'C009';
}

export type OptinDerived = 'optin' | 'always' | 'na';

/** ④ opt-in 여부 — 빌드타임 take-this.json[code].yml.alwaysOn 파생. 엔트리 부재(SUPERSEDED/미카탈로그)=na. */
export function deriveOptin(node: ModuleNode, takeThisData: TakeThisData): OptinDerived {
  const entry = takeThisData[node.code];
  if (!entry) return 'na';
  return entry.yml.alwaysOn ? 'always' : 'optin';
}

/** ⑤ 채택 biz 유무(#329 usedByBiz · priority 대체 — §R640-2 결론). */
export function deriveHasBiz(node: ModuleNode): boolean {
  return (node.usedByBiz?.length ?? 0) > 0;
}

// ─── match (URL 쿼리 값 → predicate) ──────────────────────────────────────────
// 모든 match* 는 파라미터 부재(null/undefined) 또는 알 수 없는 값(오타 딥링크)이면
// 무조건 true(미적용·방어적 파싱) — §R640-3.

/** ① 카테고리(artifact) — category 쿼리와 정확히 일치. */
export function matchCategory(node: ModuleNode, category: string | null | undefined): boolean {
  if (!category) return true;
  return node.artifact === category;
}

/** ② 상태 — status=active|superseded. */
export function matchStatus(node: ModuleNode, status: string | null | undefined): boolean {
  if (status !== 'active' && status !== 'superseded') return true;
  return deriveStatus(node) === status;
}

/** ③ 라이브 데모 유무 — demo=true|false. */
export function matchDemo(node: ModuleNode, demo: string | null | undefined): boolean {
  if (demo !== 'true' && demo !== 'false') return true;
  return deriveHasDemo(node) === (demo === 'true');
}

/** ④ opt-in 여부 — optin=true(opt-in)|false(상시 on)|na(미해당). */
export function matchOptin(node: ModuleNode, optin: string | null | undefined, takeThisData: TakeThisData): boolean {
  if (optin !== 'true' && optin !== 'false' && optin !== 'na') return true;
  const derived = deriveOptin(node, takeThisData);
  if (optin === 'true') return derived === 'optin';
  if (optin === 'false') return derived === 'always';
  return derived === 'na';
}

/** ⑤ 채택 biz 유무 — biz=true(채택됨)|false(미채택). */
export function matchBiz(node: ModuleNode, biz: string | null | undefined): boolean {
  if (biz !== 'true' && biz !== 'false') return true;
  return deriveHasBiz(node) === (biz === 'true');
}

/** 문자열 검색 — 기존 ModuleCatalogTree `filtered` useMemo 규칙과 동일(code/nameKo/nameEn/artifact includes). */
export function matchQuery(node: ModuleNode, q: string | null | undefined): boolean {
  if (!q || !q.trim()) return true;
  const query = q.toLowerCase();
  return (
    node.code.toLowerCase().includes(query) ||
    node.nameKo.toLowerCase().includes(query) ||
    node.nameEn.toLowerCase().includes(query) ||
    node.artifact.toLowerCase().includes(query)
  );
}

/**
 * ⑥ 구독 모듈만 — watch=1 이면 watchedCodes 집합 교집합(queue #677 §R677-4②). 다른
 * match* 와 달리 페이지 훅(`useWatches`) 파생 집합을 인자로 받는다(정적 파생 불가 —
 * 서버 개인화 상태). watchedCodes 는 항상 Set(로딩 중엔 호출부가 빈 Set 을 전달).
 */
export function matchWatch(node: ModuleNode, watch: string | null | undefined, watchedCodes: Set<string>): boolean {
  if (watch !== '1') return true;
  return watchedCodes.has(node.code);
}

/** distinct artifact 목록(카테고리 select 옵션용) — 알파벳 정렬. */
export function distinctArtifacts(nodes: ModuleNode[]): string[] {
  return [...new Set(nodes.map((n) => n.artifact))].sort();
}
